import { Box, Chip, Grid, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import { useCallback } from "react";
import data from '../../../data/data.json';
import './hero.css';

const HeroCharacter = () => {
    const {t} = useTranslation();
    const theme = useTheme();

    const clickDownload = useCallback(() => {
        window.open(data.store.play_store, "_blank");
    }, []);

    return (
        <>
            <Stack
                direction='row'
                justifyContent='center'
                alignItems='center'
                position='relative'
                sx={{
                    transform: {xs: 'translateY(-20px)', md: 'translateY(-50px)', lg: 'translateY(-100px)'}
                }}
            >
                {/* Character */}
                <Box
                    sx={{
                        width: '35%',
                        zIndex: 4,
                    }}
                >
                    <div
                        data-aos="zoom-out-up"
                        data-aos-easing="ease-out-back"
                        data-aos-delay="300"
                        data-aos-duration="1000"
                    >
                        <img 
                            src="/images/game/Penguin.gif" 
                            alt="Character" 
                            width='100%'
                        />
                    </div>
                </Box>

                {/* Left Section */}
                <Stack
                    direction='column'
                    justifyContent='center'
                    alignItems='start'
                    position='absolute'
                    width={{xs: '30%', md: '15%'}}
                    top={0}
                    left={0}
                    sx={{
                        transform: {
                            xs: 'translate(0px, -10px)', 
                            sm: 'translate(0px, 100px)', 
                            md: 'translate(100px, 180px)'
                        },
                    }}
                    spacing={{xs: 1, md: 2}}
                >
                    {/* Myself */}
                    <Typography
                        sx={{
                            fontWeight: 400,
                            color: 'white',
                            fontSize: {xs: 12, sm: 14, md: 16}
                        }}
                        data-aos="fade-right"
                        data-aos-delay="300"
                        data-aos-duration="1000"
                    >
                        {t('hero.penguin.myself')}
                    </Typography>

                    {/* Download */}
                    <Chip
                        label={t('hero.penguin.download')}
                        onClick={clickDownload}
                        onDelete={clickDownload}
                        size="small"
                        deleteIcon={
                            <ArrowOutwardIcon 
                                sx={{
                                    backgroundColor: 'white',
                                    borderRadius: '50%',
                                    display: {xs: 'none', md: 'block'}
                                }}
                            />
                        }
                        sx={{
                            backgroundColor: theme.palette.secondary.main,
                            color: 'white',
                            fontWeight: 600,
                            fontSize: {xs: 10, md: 16},
                            py: {xs: 1, md: 2.25},
                            px: {xs: 0, md: 0.5},
                            '&: hover': {
                                backgroundColor: theme.palette.secondary.main,
                                transform: 'scale(0.9)'
                            },
                            animation: 'downloadBtn 2s infinite 1s'
                        }}
                        data-aos="fade-right"
                        data-aos-delay="500"
                        data-aos-duration="1000"
                    />

                    {/* Arrow */}
                    <Box
                        sx={{
                            position: 'absolute',
                            width: {xs: '75%', sm: '50%', md: '100%'},
                            transform: {xs: 'translate(40px, 80px)', md: 'translate(100px, 100px)'}
                        }}
                    >
                        <div
                            data-aos="fade-right"
                            data-aos-delay="700"
                            data-aos-duration="1000"
                        >
                            <img 
                                src="/images/vectors/Arrow-1.png" 
                                alt="Arrow" 
                                width='100%'
                                style={{
                                    animation: 'arrowLeft 1s infinite'
                                }}
                            />
                        </div>
                    </Box>
                </Stack>

                {/* Right Section */}
                <Stack
                    direction='column'
                    justifyContent='center'
                    alignItems={{xs: 'end', md: 'start'}}
                    position='absolute'
                    width={{xs: '30%', md: '17%', lg: '15%'}}
                    top={0}
                    right={0}
                    sx={{
                        transform: {
                            xs: 'translate(0px, 30px)', 
                            sm: 'translate(0px, 105px)', 
                            md: 'translate(-100px, 230px)'
                        }
                    }}
                    spacing={{xs: 1, md: 2}}
                >
                    {/* Title */}
                    <Typography
                        sx={{
                            fontWeight: 400,
                            color: 'white',
                            fontSize: {xs: 12, md: 16},
                            textAlign: {xs: 'right', md: 'left'}
                        }}
                        data-aos="fade-up"
                        data-aos-delay="400"
                        data-aos-duration="1000"
                    >
                        {t('hero.penguin.brief_features.title')}
                    </Typography>

                    {/* Arrow */}
                    <Box
                        sx={{
                            position: 'absolute',
                            width: {xs: '30%', sm: '40%', md: '50%'},
                            transform: {
                                xs: 'translate(-70px, -50px)', 
                                sm: 'translate(-130px, -50px)', 
                                md: 'translate(-100px, -75px)', 
                                lg: 'translate(-130px, -75px)'
                            }
                        }}
                    >
                        <div
                            data-aos="fade-up"
                            data-aos-delay="800"
                            data-aos-duration="1000"
                        >
                            <img 
                                src="/images/vectors/Arrow-2.png" 
                                alt="Arrow" 
                                width='100%'
                                style={{
                                    animation: 'arrowRight 2s infinite'
                                }}
                            />
                        </div>
                    </Box>

                    {/* Features */}
                    <Grid
                        direction='row'
                        justifyContent='start'
                        alignItems='center'
                        container
                        width={{xs: '60%', sm: '50%', md: '75%'}}
                        spacing={{xs: 0.5, md: 1}}
                    >
                        <Grid size={6}>
                            <Tooltip title={t('hero.penguin.brief_features.feature-1')} arrow>
                                <Box
                                    sx={{
                                        width: '100%'
                                    }}
                                    data-aos="flip-left"
                                    data-aos-delay="600"
                                    data-aos-duration="1000"
                                >
                                    <img 
                                        src="/images/features/Feature-1.png" 
                                        alt={t('hero.penguin.brief_features.feature-1')}
                                        width='100%'
                                        style={{
                                            animation: 'feature1 2s infinite linear 1s'
                                        }}
                                    />
                                </Box>
                            </Tooltip>
                        </Grid>

                        <Grid size={6}>
                            <Tooltip title={t('hero.penguin.brief_features.feature-2')} arrow>
                                <Box
                                    sx={{
                                        width: '100%'
                                    }}
                                    data-aos="flip-left"
                                    data-aos-delay="650"
                                    data-aos-duration="1000"
                                >
                                    <img 
                                        src="/images/features/Feature-2.png" 
                                        alt={t('hero.penguin.brief_features.feature-2')}
                                        width='100%'
                                        style={{
                                            animation: 'feature2 2s infinite linear 1s'
                                        }}
                                    />
                                </Box>
                            </Tooltip>
                        </Grid>

                        <Grid size={6}>
                            <Tooltip title={t('hero.penguin.brief_features.feature-3')} arrow>
                                <Box
                                    sx={{
                                        width: '100%'
                                    }}
                                    data-aos="flip-left"
                                    data-aos-delay="700"
                                    data-aos-duration="1000"
                                >
                                    <img 
                                        src="/images/features/Feature-3.png" 
                                        alt={t('hero.penguin.brief_features.feature-3')}
                                        width='100%'
                                        style={{
                                            animation: 'feature3 2s infinite linear 1s'
                                        }}
                                    />
                                </Box>
                            </Tooltip>
                        </Grid>

                        <Grid size={6}>
                            <Tooltip title={t('hero.penguin.brief_features.feature-4')} arrow>
                                <Box
                                    sx={{
                                        width: '100%'
                                    }}
                                    data-aos="flip-left"
                                    data-aos-delay="750"
                                    data-aos-duration="1000"
                                >
                                    <img 
                                        src="/images/features/Feature-4.png" 
                                        alt={t('hero.penguin.brief_features.feature-4')}
                                        width='100%'
                                        style={{
                                            animation: 'feature4 2s infinite linear 1s'
                                        }}
                                    />
                                </Box>
                            </Tooltip>
                        </Grid>
                    </Grid>
                </Stack>
            </Stack>
        </>
    );
}

export default HeroCharacter;